import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { ArrowRight } from "@phosphor-icons/react/ArrowRight";
import { CreditCard } from "@phosphor-icons/react/CreditCard";
import { Lock } from "@phosphor-icons/react/Lock";
import { PencilSimple } from "@phosphor-icons/react/PencilSimple";
import { Receipt } from "@phosphor-icons/react/Receipt";
import NextLink from "next/link";
import { FC } from "react";
import { useCheckoutPricing } from "utils";
import { useAuthContext } from "~auth/contexts/auth";
import { PropertyItem } from "~ui-components/components/atoms/property-item";
import { PropertyList } from "~ui-components/components/atoms/property-list";
import {
	Account,
	PreviewSubscriptionChangeResults,
	SUBSCRIPTION_CYCLE,
	SUBSCRIPTION_TYPE,
} from "~ui-components/types/__generated/gql/graphql";
import { getRecurlyHostedBillingInfoUrl } from "~utils/recurly/get-recurly-hosted-billing-info-url";
import { EditSubscriptionProps, PLAN_NAME } from "../edit-subscription";

const BILLING_CYCLE_DESCRIPTION: Record<SUBSCRIPTION_CYCLE, string> = {
	MONTHLY: "Per month",
	ANNUAL: "Per year",
	TWO_YEARS: "Per 2 years",
	THREE_YEARS: "Per 3 years",
	FOUR_YEARS: "Per 4 years",
	FIVE_YEARS: "Per 5 years",
};

const formatter = new Intl.NumberFormat("en-US", {
	minimumFractionDigits: 2,
	maximumFractionDigits: 2,
});

export const AccountEditSubscriptionSummary: FC<
	EditSubscriptionProps & {
		onContinueTrial: () => void;
		account: Account;
		subscriptionChangePreview?: PreviewSubscriptionChangeResults;
		isSubscriptionChangeLoading?: boolean;
	}
> = ({
	form,
	onContinueTrial,
	account,
	subscriptionChangePreview,
	isSubscriptionChangeLoading,
}) => {
	const { isTrialExpired } = useAuthContext();
	const { pricing, pricingError } = useCheckoutPricing(
		// @ts-ignore
		form.values,
		// @ts-ignore
		account._recurlyAccountInfo?.billingAddress,
	);
	const isGB = account._recurlyAccountInfo?.billingAddress?.country === "GB";

	return (
		<Stack spacing={4}>
			<Card>
				<CardHeader
					title="Subscription summary"
					subheader={
						<Stack
							direction="row"
							spacing={1}
							alignItems="center"
							color="text.primary"
							mt={2}>
							<Typography variant="h4">
								{pricing.price?.currency?.symbol ?? "$"}{" "}
								{pricing.price?.now?.total ?? "0.00"}
							</Typography>
							<Typography
								variant="caption"
								color="text.secondary">
								{BILLING_CYCLE_DESCRIPTION[form.values.cycle]}
								{isGB ? " incl. UK VAT" : ""}
							</Typography>
						</Stack>
					}
				/>
				<PropertyList
					divider={<Divider />}
					sx={{
						"--PropertyItem-padding": "12px 24px",
						"--PropertyItem-Value-Align": "right",
					}}>
					<PropertyItem
						name={
							<Typography
								color="text.primary"
								variant="body2">
								{PLAN_NAME[form.values.plan]}
							</Typography>
						}
						value={
							<Typography
								align="right"
								color="text.primary"
								variant="body2">
								{pricing.price?.currency?.symbol ?? "$"}{" "}
								{pricing.price?.now?.items?.[0]?.plan ?? "0.00"}
							</Typography>
						}
					/>
					<PropertyItem
						name={
							<Stack spacing={1}>
								<Typography
									color="text.primary"
									variant="body2">
									Add-ons
								</Typography>
							</Stack>
						}
						value={
							<Typography
								align="right"
								color="text.primary"
								variant="body2">
								{pricing.price?.currency?.symbol ?? "$"}{" "}
								{pricing.price?.now?.items?.[0]?.addons ?? "0.00"}
							</Typography>
						}
					/>
					<PropertyItem
						name={
							<Typography
								color="text.primary"
								variant="subtitle2">
								Subtotal{isGB ? " (ex-VAT)" : ""}
							</Typography>
						}
						value={
							<Typography
								align="right"
								color="text.primary"
								variant="subtitle2">
								{pricing.price?.currency?.symbol ?? "$"}{" "}
								{pricing.price?.now?.subtotal ?? "0.00"}
							</Typography>
						}
					/>
					{account.subscription.type === SUBSCRIPTION_TYPE.TRIAL ? (
						<PropertyItem
							name={
								<Stack spacing={1}>
									<Typography
										color="text.primary"
										variant="body2">
										Taxes
									</Typography>
									<Typography
										color="text.secondary"
										variant="caption">
										Calculated at checkout
									</Typography>
								</Stack>
							}
							value={null}
						/>
					) : isGB ? (
						<PropertyItem
							name={
								<Stack spacing={1}>
									<Typography
										color="text.primary"
										variant="body2">
										VAT {+(pricing.price?.taxes?.[0]?.rate ?? 0) * 100}%
									</Typography>
									{!!pricing.price?.taxes?.length &&
										pricing.price.taxes.map((tax) => (
											<Typography
												key={tax.region}
												color="text.secondary"
												variant="caption">
												{tax.region} VAT {+(tax.rate ?? 0) * 100}%
											</Typography>
										))}
								</Stack>
							}
							value={
								<Typography
									align="right"
									color="text.primary"
									variant="body2">
									{pricing.price?.currency?.symbol ?? "$"}{" "}
									{pricing.price?.now?.taxes ?? "0.00"}
								</Typography>
							}
						/>
					) : null}
					<PropertyItem
						name={
							<Typography
								color="text.primary"
								variant="subtitle2">
								Due today
							</Typography>
						}
						value={
							account.subscription.type === SUBSCRIPTION_TYPE.PAID ? (
								<Typography
									align="right"
									color="text.primary"
									variant="subtitle2">
									{pricing.price?.currency?.symbol ?? "$"}{" "}
									{formatter.format(
										subscriptionChangePreview?.chargeTotal ?? 0,
									)}
								</Typography>
							) : (
								<Typography
									align="right"
									color="text.primary"
									variant="subtitle2">
									{pricing.price?.currency?.symbol ?? "$"}{" "}
									{pricing.price?.now?.total ?? "0.00"}
								</Typography>
							)
						}
					/>
					{account.subscription.type !== SUBSCRIPTION_TYPE.TRIAL &&
						!!subscriptionChangePreview?.creditTotal && (
							<PropertyItem
								name={
									<Typography
										color="text.primary"
										variant="subtitle2">
										Credit to account
									</Typography>
								}
								value={
									<Stack
										direction="row"
										spacing={1}
										alignItems="center"
										justifyContent="flex-end">
										{isSubscriptionChangeLoading && (
											<CircularProgress size="22px" />
										)}
										<Typography
											align="right"
											color="text.primary"
											variant="subtitle2">
											{pricing.price?.currency?.symbol ?? "$"}{" "}
											{formatter.format(
												subscriptionChangePreview?.creditTotal ?? 0,
											)}
										</Typography>
									</Stack>
								}
							/>
						)}
				</PropertyList>
			</Card>
			<Card
				sx={{
					bgcolor: "background.level2",
				}}>
				<CardContent>
					<Stack spacing={2}>
						{account.subscription.type === SUBSCRIPTION_TYPE.PAID ? (
							<Button
								fullWidth
								variant="contained"
								type="submit"
								disabled={
									!form.isValid ||
									form.isSubmitting ||
									pricing.loading ||
									!!isSubscriptionChangeLoading ||
									!!pricingError
								}>
								Update subscription
							</Button>
						) : (
							<Stack
								spacing={2}
								direction={"row"}
								alignItems={"center"}>
								{!isTrialExpired && (
									<Button
										variant="text"
										color="inherit"
										sx={{
											minWidth: "120px",
											typography: "body2",
										}}
										onClick={onContinueTrial}
										disabled={
											!form.isValid ||
											form.isSubmitting ||
											pricing.loading ||
											!!isSubscriptionChangeLoading ||
											!!pricingError
										}>
										Continue trial
									</Button>
								)}
								<Button
									fullWidth
									variant="contained"
									type="submit"
									disabled={
										!form.isValid ||
										form.isSubmitting ||
										pricing.loading ||
										!!isSubscriptionChangeLoading ||
										!!pricingError
									}
									endIcon={<ArrowRight />}>
									Checkout with Recurly
								</Button>
							</Stack>
						)}
						<Stack
							padding={2}
							spacing={2}>
							<Stack
								direction="row"
								spacing={2}
								sx={{ alignItems: "center" }}>
								<Box
									sx={{
										alignItems: "center",
										color: "var(--mui-palette-success-main)",
										display: "flex",
										justifyContent: "center",
									}}>
									<Lock />
								</Box>
								<Typography variant="subtitle2">Secure checkout</Typography>
							</Stack>
							<Stack spacing={2}>
								<Typography
									variant="body2"
									color={"text.secondary"}>
									Your purchases are secured by an industry best encryption
									service - Recurly
								</Typography>
							</Stack>
						</Stack>
					</Stack>
				</CardContent>
			</Card>
			{!!account._recurlyAccountInfo && (
				<Stack
					direction="row"
					alignItems="center"
					justifyContent="space-between"
					spacing={2}
					px={3}>
					<Typography
						color="text.primary"
						variant="subtitle2">
						Payment method
					</Typography>
					{account._recurlyAccountInfo.subscription?.collectionMethod ===
						"automatic" && (
						<Stack
							direction="row"
							alignItems="center"
							spacing={1}>
							<CreditCard />
							<Typography variant="caption">
								{account._recurlyAccountInfo.paymentMethod?.cardType}&nbsp;
								{account._recurlyAccountInfo.paymentMethod?.lastFour}&nbsp;(
								{account._recurlyAccountInfo.paymentMethod?.expMonth}/
								{account._recurlyAccountInfo.paymentMethod?.expYear
									?.toString()
									.slice(-2)}
								)
							</Typography>
							<Link
								component={NextLink}
								href={getRecurlyHostedBillingInfoUrl(
									account._recurlyAccountInfo.hostedToken ?? "",
								)}
								target="_blank"
								color="unset"
								mt={0.5}>
								<PencilSimple />
							</Link>
						</Stack>
					)}
					{account._recurlyAccountInfo.subscription?.collectionMethod ===
						"manual" && (
						<Stack
							direction="row"
							alignItems="center"
							spacing={1}>
							<Receipt />
							<Typography variant="caption">We&apos;ll invoice you</Typography>
						</Stack>
					)}
				</Stack>
			)}
		</Stack>
	);
};
